import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Twitter, MapPin, Phone, Mail } from "lucide-react";
import { BUSINESS } from "@/lib/config";

const QUICK_LINKS = [
  { label: "About", href: "#about" },
  { label: "Products", href: "#products" },
  { label: "Brands", href: "#brands" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

const CATEGORIES = [
  "Modular Switches",
  "Wires & Cables",
  "LED Lighting",
  "Fans",
  "Switchgear",
  "Geysers",
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#001A4A] text-white pt-16 pb-6 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-white flex items-center justify-center overflow-hidden">
                <img
                  src={BUSINESS.logoUrl}
                  alt="Master Mall Nashik logo"
                  className="w-full h-full object-contain p-1"
                />
              </div>
              <div>
                <div className="font-display font-bold text-white text-lg">
                  Master Mall
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#FFD500] font-medium">
                  Nashik
                </div>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed">
              Nashik&apos;s trusted destination for premium electrical products,
              serving homes, businesses and industries with quality and honest
              pricing.
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { Icon: Facebook, href: BUSINESS.socials.facebook, name: "facebook" },
                { Icon: Instagram, href: BUSINESS.socials.instagram, name: "instagram" },
                { Icon: Youtube, href: BUSINESS.socials.youtube, name: "youtube" },
                { Icon: Twitter, href: BUSINESS.socials.twitter, name: "twitter" },
              ].map(({ Icon, href, name }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${name}`}
                  aria-label={name}
                  className="w-9 h-9 bg-white/5 border border-white/10 hover:bg-[#FFD500] hover:text-[#00296B] flex items-center justify-center transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-widest mb-5">
              Explore
            </h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/70 hover:text-[#FFD500] text-sm transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-widest mb-5">
              Products
            </h4>
            <ul className="space-y-3">
              {CATEGORIES.map((c) => (
                <li key={c}>
                  <a
                    href="#products"
                    className="text-white/70 hover:text-[#FFD500] text-sm transition-colors"
                  >
                    {c}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-widest mb-5">
              Reach Us
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 text-white/70">
                <MapPin className="w-4 h-4 text-[#FFD500] flex-shrink-0 mt-0.5" />
                <span>
                  {BUSINESS.address.line1} {BUSINESS.address.line2}{" "}
                  {BUSINESS.address.line3}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#FFD500] flex-shrink-0" />
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  data-testid="footer-phone"
                  className="text-white/70 hover:text-[#FFD500] transition-colors"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#FFD500] flex-shrink-0" />
                <a
                  href={`mailto:${BUSINESS.email}`}
                  data-testid="footer-email"
                  className="text-white/70 hover:text-[#FFD500] transition-colors break-all"
                >
                  {BUSINESS.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-white/50 text-xs">
          <div data-testid="footer-copyright">
            © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-5">
            <Link
              to="/privacy-policy"
              data-testid="footer-privacy-link"
              className="hover:text-[#FFD500] transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="w-px h-3 bg-white/20" />
            <Link
              to="/terms-conditions"
              data-testid="footer-terms-link"
              className="hover:text-[#FFD500] transition-colors"
            >
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
